import { NextPage } from "next";
import Head from "next/head";
import { useEffect, useState } from "react";
import Button from "@/components/Button";
import LoadingPage from "@/components/LoadingPage";
import { useSegmentCallbackV2 } from "@/src/segment";

import { WithSidebar } from "@/components/Sidebar";
import useEnv from "@/src/useEnv";
import useUser from "@/src/useUser";
import { usePolicyAutosave } from "@/src/policyAutosave";
import ProductionEnvironmentWarningBanner from "@/components/ProductionEnvironmentWarningBanner";
import Modal from "@/components/Modal";
import { EnvData } from "@/src/types";
import {
  usePersistedWorkbenchState,
  isDefaultWorkbenchState,
  DEFAULT_WORKBENCH_POLICY,
  workbenchToPolicy,
  WorkbenchPolicy,
} from "@/src/workbenchLogic";
import createPersistedState from "@/src/createPersistedState";

import Workbench from "@/components/rules-editor/Workbench";
import PolicyEditorLoader from "@/components/rules-editor/CodeEditor";

type EditorPreference = { preferredMode: "code" | "workbench" | "none" };

const usePersistedState = createPersistedState<
  Partial<{
    [env: string]: EditorPreference;
  }>
>("rules_editor_prefs");

const usePersistedPrefs = (
  selectedEnv: EnvData
): [EditorPreference, (update: EditorPreference) => void] => {
  const [states, setStates] = usePersistedState({});
  const envState = states[selectedEnv.pub_id] ?? { preferredMode: "none" };
  return [
    envState,
    (update: EditorPreference) => {
      setStates({ ...states, [selectedEnv.pub_id]: update });
    },
  ];
};

// Component to guard against null `selectedEnv`, to make it possible to abide by hook rules
const RulesEditorGuarded: React.FC<{ selectedEnv: EnvData }> = ({
  selectedEnv,
}) => {
  const [workbenchState, setPersistedWorkbenchState] =
    usePersistedWorkbenchState(selectedEnv);
  const [_, setCodeModeAutosave] = usePolicyAutosave(selectedEnv);

  const [prefs, setPrefs] = usePersistedPrefs(selectedEnv);
  const [mode, setMode] = [
    prefs.preferredMode,
    (preferredMode: "code" | "workbench") => {
      setPrefs({ ...prefs, preferredMode });
    },
  ];

  const [policyLoading, setPolicyLoading] = useState<boolean>(true);
  useEffect(() => {
    if (prefs.preferredMode === "none") {
      fetch(`/web/environment/${selectedEnv.pub_id}/api/policy`)
        .then((resp) => {
          if (resp.ok) {
            return resp.json();
          } else {
            throw "Error fetching policy";
          }
        })
        .then((policy: { policy?: { src: string } }) => {
          // Who gets which editor?
          // - if you have a policy but no non-default workbench state, you're in code mode
          // - otherwise, you're in workbench mode
          const useCodeMode =
            isDefaultWorkbenchState(workbenchState) &&
            policy?.policy?.src !== "" &&
            policy?.policy?.src !== workbenchToPolicy(DEFAULT_WORKBENCH_POLICY);

          setMode(useCodeMode ? "code" : "workbench");
        })
        .catch((err) => {
          console.error(err);
          setMode("code"); // Sure why not
        })
        .finally(() => {
          setPolicyLoading(false);
        });
    } else {
      setPolicyLoading(false);
    }
  }, [prefs.preferredMode]);

  const [isCodeWarningModalOpen, setIsCodeWarningModalOpen] =
    useState<boolean>(false);
  const onCodeWarningModalClose = () => {
    setIsCodeWarningModalOpen(false);
  };

  const [isWorkbenchWarningModalOpen, setIsWorkbenchWarningModalOpen] =
    useState<boolean>(false);
  const onWorkbenchWarningModalClose = () => {
    setIsWorkbenchWarningModalOpen(false);
  };

  const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);
  const [policyImportLoading, setPolicyImportLoading] =
    useState<boolean>(false);

  const [backendWorkbenchStatePrefetched, setBackendWorkbenchStatePrefetched] =
    useState<boolean>(false);

  const track = useSegmentCallbackV2();

  if (policyLoading) {
    return <LoadingPage />;
  }

  return (
    <>
      <Head>
        <title>{unsavedChanges ? "* " : ""}Oso Cloud - Rules Editor</title>
      </Head>
      <WithSidebar>
        <div className="flex flex-col h-screen">
          <div className="shrink-0">
            <ProductionEnvironmentWarningBanner
              isProductionEnvironment={selectedEnv.name === "production"}
            />
          </div>
          <div className="grow">
            {mode === "code" ? (
              <PolicyEditorLoader
                otherToolbarButtons={
                  <Button
                    medium
                    onClick={() => setIsWorkbenchWarningModalOpen(true)}
                  >
                    Switch to workbench mode
                  </Button>
                }
                onUnsavedChangesChange={(unsavedChanges) => {
                  setUnsavedChanges(unsavedChanges);
                }}
              />
            ) : (
              <Workbench
                onUnsavedChangesChange={(unsavedChanges) => {
                  setUnsavedChanges(unsavedChanges);
                }}
                selectedEnv={selectedEnv}
                hideWhenWouldIUse
                fetchFromBackend
                backendStatePrefetched={backendWorkbenchStatePrefetched}
                header={
                  <>
                    Rules Editor{" "}
                    <span className="text-lg font-sans">Workbench mode</span>
                  </>
                }
                otherToolbarButtons={
                  <Button
                    medium
                    onClick={() => setIsCodeWarningModalOpen(true)}
                  >
                    Switch to code mode
                  </Button>
                }
              />
            )}
          </div>
        </div>
        <Modal
          isOpen={isCodeWarningModalOpen}
          onClose={onCodeWarningModalClose}
        >
          <div className="font-space text-3xl">Switch to code mode?</div>
          <div className="mt-4" />
          <div>
            Code mode lets you write your policy rules directly in the Polar
            language.
          </div>
          <div className="mt-2" />
          <div>
            <strong>
              Code mode is a feature {"that's"} best for experienced users.
            </strong>{" "}
            If {"you're"} just getting started with Oso Cloud, we recommend
            staying in workbench mode.
          </div>

          <div className="mt-4" />
          <div className="flex flex-row justify-around">
            <Button medium onClick={onCodeWarningModalClose}>
              Cancel
            </Button>

            <Button
              medium
              theme="primary"
              onClick={() => {
                track("Switch to code mode", { unsavedChanges });
                setPersistedWorkbenchState(DEFAULT_WORKBENCH_POLICY);
                setBackendWorkbenchStatePrefetched(false);
                setMode("code");
                onCodeWarningModalClose();
              }}
            >
              {unsavedChanges
                ? "Discard undeployed changes and switch to code mode"
                : "Switch to code mode"}
            </Button>
          </div>
        </Modal>

        <Modal
          isOpen={isWorkbenchWarningModalOpen}
          onClose={onWorkbenchWarningModalClose}
        >
          <div className="font-space text-3xl">Switch to workbench mode?</div>
          <div className="mt-4" />
          <div>Workbench mode lets you visually edit your policy rules.</div>
          <div className="mt-2" />
          <div>When you switch to workbench mode:</div>
          <div className="mt-2" />
          <div>
            <ul className="list-disc list-inside">
              <li>Comments in your policy will be removed</li>
              <li>
                The order of resource blocks and rules in your policy may change
              </li>
              <li>
                Certain shorthand rules may be rewritten as long-form rules
              </li>
            </ul>
          </div>
          <div className="mt-2" />
          <div>
            These changes are only cosmetic&mdash;{" "}
            <em>the underlying logic of your rules will not be affected.</em>
          </div>

          <div className="mt-4" />
          <div className="flex flex-row justify-around">
            <Button
              medium
              onClick={onWorkbenchWarningModalClose}
              disabled={policyImportLoading}
            >
              Cancel
            </Button>

            <Button
              medium
              theme="primary"
              loading={policyImportLoading}
              disabled={policyImportLoading}
              onClick={() => {
                track("Switch to workbench mode", { unsavedChanges });
                setPolicyImportLoading(true);
                fetch(
                  `/web/environment/${selectedEnv.pub_id}/api/policy_workbench`
                )
                  .then((r) => (r.ok ? r.json() : Promise.reject(r)))
                  .then((state: { policy?: WorkbenchPolicy }) => {
                    setPersistedWorkbenchState(
                      state.policy || DEFAULT_WORKBENCH_POLICY // hmm
                    );
                    setCodeModeAutosave(null);
                    setBackendWorkbenchStatePrefetched(true);
                    setMode("workbench");
                    onWorkbenchWarningModalClose();
                  })
                  .catch((e) => {
                    console.error(e);
                  })
                  .finally(() => {
                    setPolicyImportLoading(false);
                  });
              }}
            >
              {unsavedChanges
                ? "Discard undeployed changes and switch to workbench mode"
                : "Switch to workbench mode"}
            </Button>
          </div>
        </Modal>
      </WithSidebar>
    </>
  );
};

const RulesEditor: NextPage = () => {
  const { isLoading: userLoading } = useUser();
  const { selectedEnv } = useEnv();

  if (userLoading || !selectedEnv) {
    return <LoadingPage />;
  }
  return <RulesEditorGuarded selectedEnv={selectedEnv} />;
};

export default RulesEditor;
